import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, SettingsRow, SettingsSection } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { CalendarLinkErrorDialog } from '@/components/CalendarLinkErrorDialog.tsx';
import { DisconnectCalendarDialog } from '@/components/DisconnectCalendarDialog.tsx';
import { SlackConnectionRow } from '@/components/SlackConnectionRow.tsx';
import { StatusLine } from '@/components/StatusLine.tsx';
import {
  INTEGRATION_PROVIDERS,
  disconnectCalendar,
  fetchIntegrations,
  startCalendarLink,
  testConnection,
  type IntegrationProvider,
} from '@/lib/integrations.ts';
import { canLinkSlack } from '@/lib/slackInstallation.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/connections')({
  /*
   * With nothing this person may connect, there is no tab and nothing on the
   * page: a link or a bookmark to it lands on the first tab instead.
   */
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData({
      queryKey: ['currentUser'],
      queryFn: fetchCurrentUser,
    });

    if (!user.calendar_enabled && !canLinkSlack(user)) {
      throw redirect({ to: '/settings/profile', replace: true });
    }
  },
  component: ConnectionsRoute,
  /*
   * A provider's refusal comes back to this page as `?error=` — the flow set
   * itself up that way (`errorCallbackURL` in `startCalendarLink`), and until
   * this param was read, the refusal arrived and vanished without a word.
   */
  validateSearch: (search: Record<string, unknown>): ConnectionsSearch => ({
    error: typeof search.error === 'string' ? search.error : undefined,
    slack: typeof search.slack === 'string' ? search.slack : undefined,
  }),
});

interface ConnectionsSearch {
  /** The code a provider's refusal bounced back with. */
  error?: string;
  /** How an "Add to Slack" ended, as the API sent the browser back. */
  slack?: string;
}

/*
 * Reading the search is the route's job, so the page itself stays renderable.
 * Dismissing clears `?error=` from the address — the dialog closes because the
 * code is gone, and a reload or a shared link does not resurrect a refusal
 * that was already read.
 */
function ConnectionsRoute() {
  const { error, slack } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  return (
    <ConnectionSettingsPage
      showCalendar={user?.calendar_enabled !== false}
      showSlack={canLinkSlack(user)}
      slackResult={slack ?? null}
      linkError={error ?? null}
      onDismissLinkError={() => void navigate({ search: {}, replace: true })}
    />
  );
}

const PROVIDER_NAMES: Record<IntegrationProvider, () => string> = {
  microsoft: m.connections_microsoft,
  google: m.connections_google,
};

/** A connected calendar, by the account row that disconnecting unlinks. */
interface CalendarAccount {
  provider: IntegrationProvider;
  accountId: string;
}

/* Null while untested — "we have not asked" is not the same as "it failed". */
type TestResult = boolean | null;

export interface ConnectionSettingsPageProps {
  /** The calendar rows follow the organisation's calendar switch. */
  showCalendar?: boolean;
  /** The organisation's Slack row, for an admin. */
  showSlack?: boolean;
  /** The `?slack=` result an "Add to Slack" came back with, or null. */
  slackResult?: string | null;
  /** The `?error=` code a provider bounced back with, or null when there is none. */
  linkError?: string | null;
  /** Called when the refusal has been read; the owner clears it from the address. */
  onDismissLinkError?: () => void;
}

export function ConnectionSettingsPage({
  showCalendar = true,
  showSlack = false,
  slackResult = null,
  linkError = null,
  onDismissLinkError = () => {},
}: ConnectionSettingsPageProps) {
  const queryClient = useQueryClient();
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const [tested, setTested] = useState<Partial<Record<IntegrationProvider, TestResult>>>(
    {},
  );
  /* Which calendar the confirm is about. Null means the dialog is closed. */
  const [confirming, setConfirming] = useState<CalendarAccount | null>(null);

  const {
    data: integrations,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['integrations', 'status'],
    queryFn: fetchIntegrations,
    enabled: showCalendar,
  });

  /* Disconnecting unlinks the account row that holds the grant, so it is kept per row. */
  const accountIdOf = (provider: IntegrationProvider): string | null =>
    integrations?.find((entry) => entry.provider === provider)?.account_id ?? null;

  /* Only false asks for anything: null means the Meet audio is not this calendar's business. */
  const missesMeetAccess = (provider: IntegrationProvider): boolean =>
    integrations?.find((entry) => entry.provider === provider)?.meet_recorder_access ===
    false;

  /*
   * Connecting leaves the app entirely — the provider's consent screen is a
   * full page load — so there is no success state to render here. The mutation
   * stays pending until the browser goes.
   */
  /*
   * A granted calendar lands on the agenda — that is what someone connected it
   * to see — while a refusal still comes back to this page, where the buttons
   * to try again live. Onboarding is untouched: its own screen names its own
   * next step.
   */
  const link = useMutation({
    mutationFn: (provider: IntegrationProvider) =>
      startCalendarLink(provider, `${window.location.origin}/`, {
        meetRecorder: user?.meet_recorder_enabled === true,
      }),
  });

  const unlink = useMutation({
    mutationFn: ({ accountId }: CalendarAccount) => disconnectCalendar(accountId),
    onSuccess: (_result, { provider }) => {
      setTested((results) => ({ ...results, [provider]: null }));
      return queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const check = useMutation({
    mutationFn: testConnection,
    onSuccess: (ok, provider) => setTested((results) => ({ ...results, [provider]: ok })),
    /* A failed request is a failed test — the calendar is unreachable either way. */
    onError: (_error, provider) =>
      setTested((results) => ({ ...results, [provider]: false })),
  });

  if (showCalendar && isPending) return null;
  if (showCalendar && isError) {
    return <p className='text-base text-ink-muted'>{m.connections_load_failed()}</p>;
  }

  const busy = link.isPending || unlink.isPending;
  /* Linking asks for what the organisation needs, which is only known once the user is. */
  const linkBlocked = busy || user === undefined;
  const actionFailed = link.isError || unlink.isError;
  const slackStatus = slackResultMessage(slackResult);

  const calendarRows =
    integrations &&
    INTEGRATION_PROVIDERS.map((provider) => {
      const accountId = accountIdOf(provider);
      const askMeetAccess = accountId !== null && missesMeetAccess(provider);
      return (
        <SettingsRow
          key={provider}
          label={PROVIDER_NAMES[provider]()}
          description={askMeetAccess ? m.connections_meet_access_hint() : undefined}
        >
          {/*
           * Two buttons at most, plus the Meet grant while Google lacks it. Testing is
           * the diagnostic and reconnecting the remedy, so reconnect takes the test's
           * slot once a test has failed.
           */}
          <div className='flex flex-wrap items-center gap-2'>
            {/*
             * The result sits on the buttons' own line rather than under the
             * provider name.
             *
             * Under the name it needs a line reserved for it, or the row
             * grows the moment a test finishes and shoves the card down.
             * Reserve one and the label is two lines tall against one-line
             * buttons, so `items-center` lifts the name above them. Here
             * there is nothing to reserve: text is shorter than a button, so
             * the row measures the same whether or not it has anything to
             * say, and everything stays on one centre line.
             */}
            <TestMessage
              testing={check.isPending && check.variables === provider}
              result={tested[provider] ?? null}
            />

            {accountId !== null ? (
              <>
                {/*
                 * Granting runs the same consent flow as reconnecting, which
                 * asks for the Meet audio too: once reconnect shows, one is enough.
                 */}
                {askMeetAccess && tested[provider] !== false && (
                  <Button
                    size='sm'
                    disabled={linkBlocked}
                    onClick={() => link.mutate(provider)}
                  >
                    {m.connections_meet_access_grant()}
                  </Button>
                )}
                {tested[provider] === false ? (
                  <Button
                    size='sm'
                    variant='quiet'
                    disabled={linkBlocked}
                    onClick={() => link.mutate(provider)}
                  >
                    {m.connections_reconnect()}
                  </Button>
                ) : (
                  <Button
                    size='sm'
                    variant='quiet'
                    disabled={busy || check.isPending}
                    onClick={() => check.mutate(provider)}
                  >
                    {m.connections_test()}
                  </Button>
                )}
                <Button
                  size='sm'
                  variant='dangerQuiet'
                  disabled={busy}
                  onClick={() => setConfirming({ provider, accountId })}
                >
                  {m.connections_disconnect()}
                </Button>
              </>
            ) : (
              <Button
                size='sm'
                disabled={linkBlocked}
                onClick={() => link.mutate(provider)}
              >
                {m.connections_connect()}
              </Button>
            )}
          </div>
        </SettingsRow>
      );
    });

  return (
    <>
      <StatusLine
        message={actionFailed ? m.connections_action_failed() : slackStatus?.message}
        tone={actionFailed || slackStatus?.tone === 'danger' ? 'danger' : 'muted'}
      />

      {/*
       * Slack is set for the whole organisation, a calendar per person, so once
       * both can show they get a section each. Without Slack the page keeps its
       * single card.
       */}
      {showSlack ? (
        <>
          {showCalendar && (
            <SettingsSection title={m.connections_section_calendar()}>
              {calendarRows}
            </SettingsSection>
          )}
          <SettingsSection
            title={m.connections_section_chat()}
            description={m.connections_section_chat_desc()}
          >
            <SlackConnectionRow />
          </SettingsSection>
        </>
      ) : (
        <SettingsSection title={m.settings_tab_connections()}>
          {calendarRows}
        </SettingsSection>
      )}

      {/*
       * One dialog for the page rather than one per row: only one calendar can
       * be being disconnected at a time, and which one is the state above.
       * Failure keeps its existing home in the status line at the top — the
       * dialog is closed by then, and two places saying it is one too many.
       */}
      <DisconnectCalendarDialog
        providerName={confirming === null ? null : PROVIDER_NAMES[confirming.provider]()}
        onConfirm={() => {
          if (confirming !== null) unlink.mutate(confirming);
          setConfirming(null);
        }}
        onCancel={() => setConfirming(null)}
      />

      <CalendarLinkErrorDialog code={linkError} onClose={onDismissLinkError} />
    </>
  );
}

/*
 * A cancelled consent screen was the admin's own choice, so it says nothing.
 * Every other failure has one remedy, trying again, except a workspace that
 * belongs to another organisation: only Wundo can sort that out.
 */
function slackResultMessage(
  result: string | null,
): { message: string; tone: 'muted' | 'danger' } | null {
  switch (result) {
    case null:
    case 'denied':
      return null;
    case 'connected':
      return { message: m.connections_slack_result_connected(), tone: 'muted' };
    case 'team_taken':
      return { message: m.connections_slack_result_team_taken(), tone: 'danger' };
    default:
      return { message: m.connections_slack_result_failed(), tone: 'danger' };
  }
}

/*
 * The row says nothing until a test has said something.
 *
 * `aria-live` rather than a plain span: pressing Testen gives no visual answer
 * anywhere but this line, so a screen reader has to be told the result arrived.
 */
function TestMessage({ testing, result }: { testing: boolean; result: TestResult }) {
  return (
    <span role='status' aria-live='polite' className='text-sm text-ink-muted'>
      <TestText testing={testing} result={result} />
    </span>
  );
}

function TestText({ testing, result }: { testing: boolean; result: TestResult }) {
  if (testing) return <>{m.connections_testing()}</>;
  if (result === true)
    return <span className='text-success'>{m.connections_test_ok()}</span>;
  /* The one case worth colouring red: granted once, and no longer answering. */
  if (result === false)
    return <span className='text-danger'>{m.connections_test_failed()}</span>;

  return null;
}
